import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Flame, Sparkles, Swords, ChevronDown, Search } from "lucide-react";

const STAT = [
  { key: "tracked", label: "Tracked Competitors" },
  { key: "active_ads", label: "Active Ads" },
  { key: "new_ads", label: "Newly Detected" },
  { key: "long_running", label: "Long-Running Ads" },
  { key: "new_offers", label: "New Offers" },
];

export default function Competitors() {
  const [data, setData] = useState(null);
  const [comps, setComps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [ads, setAds] = useState([]);
  const [openAd, setOpenAd] = useState(null);
  const [counter, setCounter] = useState({ loading: false, result: null, op: null });
  const [form, setForm] = useState({ name: "", facebook_page: "", instagram: "", website: "", industry: "Travel", market: "Malaysia" });
  const [tracking, setTracking] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadAll = () => {
    api.get("/competitors/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/competitors").then((r) => { setComps(r.data); if (r.data[0]) selectComp(r.data[0]); }).catch(() => {});
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const selectComp = (c) => { setSelected(c); api.get(`/competitors/${c.id}/ads`).then((r) => setAds(r.data)).catch(() => setAds([])); };

  const track = async () => {
    if (!form.name) { toast.error("Enter a brand name"); return; }
    setTracking(true);
    try { await api.post("/competitors", form); toast.success(`Now tracking ${form.name}`); setDialogOpen(false); setForm({ ...form, name: "" }); loadAll(); }
    catch (e) { toast.error("Could not track"); } finally { setTracking(false); }
  };

  const runCounter = async (op) => {
    setCounter({ loading: true, result: null, op: op.id });
    try {
      const r = await api.post("/ai/counter-campaign", { competitor: selected?.name || "", competitor_strategy: op.competitor_strategy, gap: op.detail });
      setCounter({ loading: false, result: r.data, op: op.id });
    } catch (e) { toast.error("AI failed"); setCounter({ loading: false, result: null, op: null }); }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 animate-float-up" data-testid="competitors-panel">
      {/* Stats + Track */}
      <div className="flex flex-col lg:flex-row gap-4 lg:items-end justify-between">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 flex-1">
          {STAT.map((s) => (
            <div key={s.key} className="bg-card border border-border rounded-xl p-4">
              <p className="font-display text-2xl font-extrabold text-primary">{data.pulse[s.key]}</p>
              <p className="text-[11px] text-muted-foreground mt-1">{s.label}</p>
            </div>
          ))}
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button data-testid="track-competitor-btn" className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-semibold"><Plus className="w-4 h-4 mr-1" /> Track Competitor</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Track a Competitor</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Company / Brand</Label><Input data-testid="track-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Skyline Travel" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Facebook Page</Label><Input value={form.facebook_page} onChange={(e) => setForm({ ...form, facebook_page: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Instagram</Label><Input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} /></div>
              </div>
              <div className="space-y-1.5"><Label>Website</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Industry</Label><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Market</Label><Input value={form.market} onChange={(e) => setForm({ ...form, market: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter><Button data-testid="track-find-btn" onClick={track} disabled={tracking} className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl">{tracking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" /> Find & Track</>}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* AI Opportunity Finder */}
      <div className="space-y-3">
        <h3 className="text-xs font-mono uppercase tracking-widest text-primary flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> AI Opportunity Finder</h3>
        {data.opportunities.map((op) => (
          <div key={op.id} data-testid={`opportunity-${op.id}`} className="bg-card border border-accent/30 rounded-2xl p-5">
            <p className="font-display font-semibold text-accent">Market Opportunity: {op.title}</p>
            <p className="text-sm text-muted-foreground mt-1">{op.detail}</p>
            <p className="text-sm mt-2"><b>Opportunity:</b> {op.recommendation}</p>
            <Button data-testid={`counter-btn-${op.id}`} onClick={() => runCounter(op)} disabled={counter.loading} className="mt-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-semibold">
              {counter.loading && counter.op === op.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Swords className="w-4 h-4 mr-1" /> Counter with AI</>}
            </Button>
            {counter.result && counter.op === op.id && (
              <div className="mt-4 border-t border-border pt-4 space-y-2 text-sm animate-float-up" data-testid={`counter-result-${op.id}`}>
                <p><span className="text-xs font-mono uppercase text-muted-foreground mr-2">Strategy</span>{counter.result.recommended_strategy}</p>
                <p><span className="text-xs font-mono uppercase text-muted-foreground mr-2">Objective</span>{counter.result.objective}</p>
                <p><span className="text-xs font-mono uppercase text-muted-foreground mr-2">Creative</span>{counter.result.creative_plan?.videos} videos · {counter.result.creative_plan?.images} images · CTA {counter.result.cta}</p>
                <div><p className="text-xs font-mono uppercase text-muted-foreground">Original Hooks</p><ul className="list-disc list-inside text-muted-foreground">{(counter.result.hooks || []).map((h, i) => <li key={i}>{h}</li>)}</ul></div>
                <p className="text-xs text-accent">{counter.result.why_original}</p>
                <p className="text-xs text-muted-foreground">Budget {counter.result.budget} · {counter.result.duration}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Competitor list + ads */}
      <div className="grid lg:grid-cols-4 gap-4">
        <div className="space-y-2">
          <h3 className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Tracked</h3>
          {comps.map((c) => (
            <button key={c.id} data-testid={`competitor-${c.id}`} onClick={() => selectComp(c)} className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-colors duration-200 ${selected?.id === c.id ? "border-primary/50 bg-primary/10" : "border-border bg-card hover:bg-secondary/40"}`}>
              <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center text-primary text-xs font-bold">{c.initial}</div>
              <div className="min-w-0"><p className="text-sm font-medium truncate">{c.name}</p><p className="text-[11px] text-muted-foreground">{c.active_ads} ads · {c.industry}</p></div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3">
          <h3 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">{selected?.name} — Ads Library</h3>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {ads.map((a) => (
              <div key={a.id} data-testid={`ad-${a.id}`} className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="aspect-video bg-secondary/60 flex items-center justify-center text-muted-foreground text-xs font-mono relative">
                  {a.format}
                  {a.days >= 30 && <span className="absolute top-2 left-2 text-[10px] font-mono px-2 py-0.5 rounded-full bg-destructive/20 text-destructive flex items-center gap-1"><Flame className="w-3 h-3" />{a.days}d</span>}
                </div>
                <div className="p-3 text-xs space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">First seen {a.first_seen}</span><span className={a.status === "Active" ? "text-accent" : "text-muted-foreground"}>{a.status}</span></div>
                  <p><b>Hook:</b> {a.hook} · <b>CTA:</b> {a.cta}</p>
                  <p className="text-muted-foreground">Offer: {a.offer}</p>
                  <button data-testid={`analyze-${a.id}`} onClick={() => setOpenAd(openAd === a.id ? null : a.id)} className="text-primary hover:text-primary/80 flex items-center gap-1 mt-1">View Analysis <ChevronDown className={`w-3 h-3 transition-transform ${openAd === a.id ? "rotate-180" : ""}`} /></button>
                  {openAd === a.id && (
                    <div className="border-t border-border pt-2 mt-1 space-y-1">
                      <p><b>Angle:</b> {a.angle}</p><p><b>Trigger:</b> {a.trigger}</p>
                      <p><b>AI Creative Score:</b> <span className="text-primary font-semibold">{a.score}/100</span></p>
                      {a.signal && <p className="text-accent">{a.signal}</p>}
                      <p className="text-muted-foreground">{a.ai_note}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Comparison + Trends */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-5 overflow-x-auto scrollbar-thin">
          <h3 className="font-display font-semibold mb-3">Competitor Comparison</h3>
          <table className="w-full text-sm">
            <thead className="text-muted-foreground text-xs"><tr><th className="text-left font-medium py-1"></th>{data.comparison.columns.map((c) => <th key={c} className="text-right font-medium py-1 px-2 whitespace-nowrap">{c}</th>)}</tr></thead>
            <tbody>{data.comparison.rows.map((r) => (
              <tr key={r.label} className="border-t border-border"><td className="py-2 text-muted-foreground">{r.label}</td>{r.values.map((v, i) => <td key={i} className={`text-right py-2 px-2 font-mono ${i === 0 ? "text-accent" : ""}`}>{v}%</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
        <div className="bg-card border border-border rounded-2xl p-5">
          <h3 className="font-display font-semibold mb-3">Market Trends (30d)</h3>
          <div className="space-y-2">{data.trends.map((t) => (
            <div key={t.label} className="flex justify-between text-sm"><span className="text-muted-foreground">{t.label}</span><span className="font-medium">{t.value} <span className="text-primary font-mono text-xs">{t.metric}</span></span></div>
          ))}</div>
        </div>
      </div>
    </div>
  );
}
